import { useEffect } from 'react';
import {
    ActionItem,
    ActionItemSkeleton,
    BodyTextLarge,
    Button,
} from '@modernhealth/vital-components';
import { captureException } from '@sentry/minimal';
import { useFetchSeriesQuery } from '@modernhealth/circles-graphql/v2';
import { useTranslation } from 'react-i18next';

import { Grid } from 'src/shared/components/Grid';

import { CirclePageSection } from './CirclePageSection';

const RowContainer: React.FC<{ loading?: boolean }> = ({ children, loading }) => {
    const { t } = useTranslation('circles');

    return (
        <CirclePageSection
            header={t('sections.series.explore')}
            cta={!loading ? t('sections.series.view_all') : undefined}
            to={!loading ? '/circles/upcoming' : undefined}
            description={t('sections.series.description')}
        >
            {children}
        </CirclePageSection>
    );
};

export const ExploreSeriesRowSkeleton = () => {
    return (
        <RowContainer loading>
            <Grid minItemWidth={350}>
                <ActionItemSkeleton />
                <ActionItemSkeleton />
                <ActionItemSkeleton />
                <ActionItemSkeleton />
                <ActionItemSkeleton />
                <ActionItemSkeleton />
                <ActionItemSkeleton />
                <ActionItemSkeleton />
                <ActionItemSkeleton />
            </Grid>
        </RowContainer>
    );
};

const ExporeSeriesRow: React.FC = () => {
    const { data, loading, refetch, error } = useFetchSeriesQuery();

    const { items } = data?.circleSeriesCollection ?? {};

    useEffect(() => {
        if (loading) return;

        if (!items || items.length === 0 || error) {
            if (error) {
                captureException(error);
            } else {
                captureException(new Error('Circles failed to load'));
            }
        }
    }, [loading, items, error]);

    if (loading && !data) {
        return <ExploreSeriesRowSkeleton />;
    }

    if (!items || items.length === 0) {
        return (
            <RowContainer>
                <div
                    css={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyItems: 'center',
                    }}
                >
                    <BodyTextLarge css={{ marginBottom: 16 }}>Failed to load Circles</BodyTextLarge>
                    <Button
                        title="Try again"
                        variant="primary"
                        size="medium"
                        onClick={() => {
                            refetch();

                            captureException(
                                new Error('Circle series were refetched due to load failure.'),
                            );
                        }}
                    />
                </div>
            </RowContainer>
        );
    }

    return (
        <RowContainer>
            <Grid minItemWidth={350} maxColumns={2}>
                {items.flatMap(item => {
                    if (!item) return [];

                    return (
                        <ActionItem
                            key={`actionItemList_column_${item.id}`}
                            css={{
                                // TODO: Add line limits to ActionItem
                                p: {
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                },
                            }}
                            title={item.name}
                            description={`${item.description}`}
                            startIconUrl={item.illustration ?? undefined}
                            linkTo={`/circles/series/${item.slug}`}
                        />
                    );
                })}
            </Grid>
        </RowContainer>
    );
};

export default ExporeSeriesRow;
